import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Field, Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { SingleDatePickerReusable } from '@/components/datePickers/SingleDatePickerReusable'
import { TextField, TextArea, Select } from '@/components/forms/formik'
import { FormField } from '@/components/forms/FormField'
import { closeModal } from '@/reducers/ui/ui.redux'
import { fetchVoiceAgents } from '@/reducers/outbound/outboundCampaigns.actions'
import {
  createScheduledCall,
  fetchScheduledCalls,
} from '@/reducers/outbound/outboundScheduledCalls.actions'

import {
  TIMEZONE_OPTIONS,
  CALLING_HOUR_START_OPTIONS,
  CALLING_HOUR_END_OPTIONS,
  MIN_CALLING_HOUR,
  MAX_CALLING_HOUR,
} from '../Campaigns/ScheduleRetryPage'

import './ScheduleCallModal.scss'

const TIMING_OPTIONS = [
  { value: 'exact', label: 'Exact time' },
  { value: 'window', label: 'Time window' },
]

// Matches the core-service phone_number validator (^\+?\d{10,15}$). Users type
// spaces/dashes, so normalize before validating and sending.
const PHONE_NUMBER_RE = /^\+?\d{10,15}$/
const normalizePhoneNumber = (value) => (value || '').replace(/[\s().-]/g, '')

// extra_data maps to the agent context blob, which core-service stores as a JSON
// object -- reject arrays and primitives (including null) so the payload is always a dict.
const isValidJsonObject = (value) => {
  if (!value || !value.trim()) {
    return true
  }
  try {
    const parsed = JSON.parse(value)
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
  } catch {
    return false
  }
}

// The model requires exactly one of scheduled_time or (window_start, window_end),
// so timing gates which of those fields are required.
const validationSchema = Yup.object({
  voice_agent_id: Yup.string().required('AI agent is required'),
  phone_number: Yup.string()
    .required('Phone number is required')
    .test('valid-phone', 'Enter a valid phone number', (value) =>
      PHONE_NUMBER_RE.test(normalizePhoneNumber(value))
    ),
  timezone: Yup.string().required('Contact timezone is required'),
  scheduled_date: Yup.string().required('Date is required'),
  timing: Yup.string().required(),
  scheduled_time: Yup.string().when('timing', {
    is: 'exact',
    then: (schema) => schema.required('Call time is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  window_start: Yup.string().when('timing', {
    is: 'window',
    then: (schema) => schema.required('Window start is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  window_end: Yup.string().when('timing', {
    is: 'window',
    then: (schema) =>
      schema
        .required('Window end is required')
        .test(
          'after-start',
          'Window end must be after start',
          (value, context) => !value || value > context.parent.window_start
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  extra_data: Yup.string().test(
    'valid-json',
    'Agent context must be a valid JSON object',
    isValidJsonObject
  ),
  consent: Yup.boolean().oneOf([true], 'Consent confirmation is required'),
})

const defaultValues = {
  voice_agent_id: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  timezone: '',
  scheduled_date: '',
  timing: 'exact',
  scheduled_time: '',
  window_start: '',
  window_end: '',
  extra_data: '',
  consent: false,
}

export const ScheduleCallModal = () => {
  const dispatch = useDispatch()
  const { voiceAgents } = useSelector((state) => state.outboundCampaigns.data)

  useEffect(() => {
    dispatch(fetchVoiceAgents())
  }, [])

  const voiceAgentOptions = voiceAgents.map((agent) => ({
    value: agent.id,
    label: agent.use_case_name,
  }))

  // Default the date to today so the most common "call them today" case is one step.
  const initialValues = useMemo(
    () => ({ ...defaultValues, scheduled_date: moment().format('YYYY-MM-DD') }),
    []
  )

  const handleClose = () => {
    dispatch(closeModal())
  }

  const handleSubmit = async (values) => {
    const payload = {
      voice_agent_id: Number(values.voice_agent_id),
      phone_number: normalizePhoneNumber(values.phone_number),
      timezone: values.timezone,
      scheduled_date: values.scheduled_date,
    }
    if (values.first_name) {
      payload.first_name = values.first_name
    }
    if (values.last_name) {
      payload.last_name = values.last_name
    }
    if (values.timing === 'exact') {
      payload.scheduled_time = values.scheduled_time
    } else {
      payload.window_start = values.window_start
      payload.window_end = values.window_end
    }
    if (values.extra_data && values.extra_data.trim()) {
      payload.extra_data = JSON.parse(values.extra_data)
    }

    try {
      await dispatch(createScheduledCall(payload))
      await dispatch(fetchScheduledCalls())
      handleClose()
    } catch {
      // Error toast is handled in the action
    }
  }

  return (
    <BasicModal
      show
      title="Schedule Call"
      size="small"
      onClose={handleClose}
      canCloseOnDimmerClick={false}
      closeOnEscape={false}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({
          handleSubmit: submitForm,
          isSubmitting,
          isValid,
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
        }) => (
          <form className="field-stack" onSubmit={submitForm}>
            <Field
              name="voice_agent_id"
              label="AI Agent"
              placeholder="Select an AI agent..."
              options={voiceAgentOptions}
              component={Select}
              required
              touchOnChange
              data-testid="voice-agent-select"
            />

            <div className="field-row">
              <Field name="first_name" label="First Name" component={TextField} />
              <Field name="last_name" label="Last Name" component={TextField} />
            </div>

            <div className="field-row">
              <Field
                name="phone_number"
                label="Phone Number"
                placeholder="+1 555 123 4567"
                component={TextField}
                required
              />
              <Field
                name="timezone"
                label="Contact Timezone"
                placeholder="Select timezone"
                options={TIMEZONE_OPTIONS}
                component={Select}
                required
                touchOnChange
                data-testid="timezone-select"
              />
            </div>

            <div className="field-row">
              <FormField required error={Boolean(errors.scheduled_date && touched.scheduled_date)}>
                <label htmlFor="scheduled_date">Date</label>
                <SingleDatePickerReusable
                  date={values.scheduled_date || null}
                  onChange={(nextDate) => {
                    setFieldValue('scheduled_date', moment(nextDate).format('YYYY-MM-DD'))
                    setFieldTouched('scheduled_date', true, false)
                  }}
                  allowFutureDates
                  allowPastDates={false}
                  error={Boolean(errors.scheduled_date && touched.scheduled_date)}
                  data-testid="scheduled-date-picker"
                />
              </FormField>
              <Field
                name="timing"
                label="Timing"
                options={TIMING_OPTIONS}
                component={Select}
                touchOnChange
                data-testid="timing-select"
              />
            </div>

            {values.timing === 'exact' ? (
              <Field
                name="scheduled_time"
                label="Call At"
                type="time"
                min={MIN_CALLING_HOUR}
                max={MAX_CALLING_HOUR}
                component={TextField}
                required
              />
            ) : (
              <div className="field-row">
                <Field
                  name="window_start"
                  label="Window Start"
                  placeholder="Select start time"
                  options={CALLING_HOUR_START_OPTIONS}
                  component={Select}
                  required
                  touchOnChange
                  data-testid="window-start-select"
                />
                <Field
                  name="window_end"
                  label="Window End"
                  placeholder="Select end time"
                  options={CALLING_HOUR_END_OPTIONS}
                  component={Select}
                  required
                  touchOnChange
                  data-testid="window-end-select"
                />
              </div>
            )}

            <Field
              name="extra_data"
              label="Agent Context (JSON)"
              placeholder='{"account_id": "8821", "last_pledge": "$50"}'
              component={TextArea}
              rows={4}
            />
            <div className="form-caption">
              Passed to the agent at call time — merge fields, account data, anything the script
              needs.
            </div>

            <Banner caution shrunk className="schedule-call-consent">
              <Checkbox
                id="consent"
                name="consent"
                checked={values.consent}
                onChange={(event, { checked }) => {
                  setFieldValue('consent', checked)
                  setFieldTouched('consent', true, false)
                }}
                data-testid="checkbox-consent"
                label="I confirm this contact is not on a DNC list and has provided the consent required to receive this call."
              />
            </Banner>

            <div className="form-actions is-modal">
              <Button type="button" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                brand
                type="submit"
                loading={isSubmitting}
                disabled={!isValid || isSubmitting}
                data-testid="schedule-call-submit"
              >
                Schedule Call
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </BasicModal>
  )
}
